import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, Button, TextField, Box, Typography, Grid, MenuItem, Avatar, IconButton, FormHelperText, DialogContentText, DialogActions, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import moment from "moment/moment";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import Select from "react-select";
import { useUpdateInvoiceMutation } from "store/slices/apis/invoiceApiSlice";
import CalenderSVG from "assets/logo/Calendar";
import { RxCross2 } from "react-icons/rx";
import InvoiceCommentList from "./InvoiceCommentList";
import AddInvoiceComment from "./AddInvoiceComment";

const rightLabelStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
};

const rightTextStyle = {
    "& .MuiInputBase-input": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0.16px",
        color: "#0A0A0C",
        fontFamily: "Outfit, sans-serif",
    },
};

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const EditInvoice = (props) => {
    const { open, handleClose, rowData, vendorNameOptions, statusOptions } = props;
    const { projectId } = useParams();
    const dispatch = useDispatch();

    const [updateInvoice, { isLoading }] = useUpdateInvoiceMutation();

    const [formData, setFormData] = useState({
        vendorName: rowData?.vendorName || "",
        woContractNo: rowData?.woContractNo || "",
        invoiceParticulars: rowData?.invoiceParticulars || "",
        type: rowData?.type || "",
        invoiceNo: rowData?.invoiceNo || "",
        invoiceDate: rowData?.invoiceDate
            ? moment.utc(rowData.invoiceDate).format("YYYY-MM-DD")
            : null,
        totalInvoiceAmountIncGst: rowData?.totalInvoiceAmountIncGst || "",
        receivedFromVendor: rowData?.receivedFromVendor
            ? moment.utc(rowData.receivedFromVendor).format("YYYY-MM-DD")
            : null,
        invoiceReceivedForPmoReview: rowData?.invoiceReceivedForPmoReview
            ? moment.utc(rowData.invoiceReceivedForPmoReview).format("YYYY-MM-DD")
            : null,
        pmoReview: rowData?.pmoReview
            ? moment.utc(rowData.pmoReview).format("YYYY-MM-DD")
            : null,
        // comments: rowData?.comments || "",
        pmoReviewAfterCommentsIncorporated: rowData?.pmoReviewAfterCommentsIncorporated
            ? moment.utc(rowData.pmoReviewAfterCommentsIncorporated).format("YYYY-MM-DD")
            : null,
        sentToApprover1: rowData?.sentToApprover1
            ? moment.utc(rowData.sentToApprover1).format("YYYY-MM-DD")
            : null,
        approver1: rowData?.approver1 || "",
        approver1ApprovalDate: rowData?.approver1ApprovalDate
            ? moment.utc(rowData.approver1ApprovalDate).format("YYYY-MM-DD")
            : null,
        sentToApprover2: rowData?.sentToApprover2
            ? moment.utc(rowData.sentToApprover2).format("YYYY-MM-DD")
            : null,
        approver2: rowData?.approver2 || "",
        approver2ApprovalDate: rowData?.approver2ApprovalDate
            ? moment.utc(rowData.approver2ApprovalDate).format("YYYY-MM-DD")
            : null,
        sentToApprover3: rowData?.sentToApprover3
            ? moment.utc(rowData.sentToApprover3).format("YYYY-MM-DD")
            : null,
        approver3: rowData?.approver3 || "",
        approver3ApprovalDate: rowData?.approver3ApprovalDate
            ? moment.utc(rowData.approver3ApprovalDate).format("YYYY-MM-DD")
            : null,
        sentToApprover4: rowData?.sentToApprover4
            ? moment.utc(rowData.sentToApprover4).format("YYYY-MM-DD")
            : null,
        approver4: rowData?.approver4 || "",
        approver4ApprovalDate: rowData?.approver4ApprovalDate
            ? moment.utc(rowData.approver4ApprovalDate).format("YYYY-MM-DD")
            : null,
        sentToApprover5: rowData?.sentToApprover5
            ? moment.utc(rowData.sentToApprover5).format("YYYY-MM-DD")
            : null,
        approver5: rowData?.approver5 || "",
        approver5ApprovalDate: rowData?.approver5ApprovalDate
            ? moment.utc(rowData.approver5ApprovalDate).format("YYYY-MM-DD")
            : null,
        financeSubmissionDate: rowData?.financeSubmissionDate
            ? moment.utc(rowData.financeSubmissionDate).format("YYYY-MM-DD")
            : null,
        paymentDate: rowData?.paymentDate
            ? moment.utc(rowData.paymentDate).format("YYYY-MM-DD")
            : null,
        paidAmount: rowData?.paidAmount || "",
        status: rowData?.status || "",
        holdReasonObservations: rowData?.holdReasonObservations || "",
        utrNo: rowData?.utrNo || "",
        projectId: projectId,
    });

    const validationSchema = Yup.object({
        vendorName: Yup.string().required("Vendor Name field is required"),
        invoiceParticulars: Yup.string().required("Invoice Particulars field is required"),
        invoiceNo: Yup.string().required("Invoice No. field is required"),
        //invoiceDate: Yup.date().required("Invoice Date field is required"),
        totalInvoiceAmountIncGst: Yup.string()
            .required("Total Invoice Amount field is required")
            .matches(/^\d+(\.\d+)?$/, "Only numbers and decimals are allowed (no commas)"),
        //receivedFromVendor: Yup.date().required("Received From Vendor field is required"),
        //invoiceReceivedForPmoReview: Yup.date().required("Invoice Received For PMO Review field is required"),
        paidAmount: Yup.string()
            .nullable()
            .notRequired()
            .test(
                "is-valid-number",
                "Only numbers and decimals are allowed (no commas)",
                (value) => {
                    if (!value) return true;
                    return /^\d+(\.\d+)?$/.test(value);
                }
            ),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {

        let reqBody = {
            invoiceApprovalId: rowData?.invoiceApprovalId,
            projectId: projectId,
            vendorName: values?.vendorName,
            woContractNo: values?.woContractNo,
            invoiceParticulars: values?.invoiceParticulars,
            type: values?.type,
            invoiceNo: values?.invoiceNo,
            invoiceDate: values?.invoiceDate
                ? moment.utc(values?.invoiceDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            totalInvoiceAmountIncGst: values?.totalInvoiceAmountIncGst?.toString().trim(),
            receivedFromVendor: values?.receivedFromVendor
                ? moment.utc(values?.receivedFromVendor, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            invoiceReceivedForPmoReview: values?.invoiceReceivedForPmoReview
                ? moment.utc(values?.invoiceReceivedForPmoReview, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            pmoReview: values?.pmoReview
                ? moment.utc(values?.pmoReview, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            // comments: [
            //     {
            //         comments: values?.comments,
            //     },
            // ],
            pmoReviewAfterCommentsIncorporated: values?.pmoReviewAfterCommentsIncorporated
                ? moment.utc(values?.pmoReviewAfterCommentsIncorporated, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            sentToApprover1: values?.sentToApprover1
                ? moment.utc(values?.sentToApprover1, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            approver1: values?.approver1,
            approver1ApprovalDate: values?.approver1ApprovalDate
                ? moment.utc(values?.approver1ApprovalDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            sentToApprover2: values?.sentToApprover2
                ? moment.utc(values?.sentToApprover2, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            approver2: values?.approver2,
            approver2ApprovalDate: values?.approver2ApprovalDate
                ? moment.utc(values?.approver2ApprovalDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            sentToApprover3: values?.sentToApprover3
                ? moment.utc(values?.sentToApprover3, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            approver3: values?.approver3,
            approver3ApprovalDate: values?.approver3ApprovalDate
                ? moment.utc(values?.approver3ApprovalDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            sentToApprover4: values?.sentToApprover4
                ? moment.utc(values?.sentToApprover4, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            approver4: values?.approver4,
            approver4ApprovalDate: values?.approver4ApprovalDate
                ? moment.utc(values?.approver4ApprovalDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            sentToApprover5: values?.sentToApprover5
                ? moment.utc(values?.sentToApprover5, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            approver5: values?.approver5,
            approver5ApprovalDate: values?.approver5ApprovalDate
                ? moment.utc(values?.approver5ApprovalDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            financeSubmissionDate: values?.financeSubmissionDate
                ? moment.utc(values?.financeSubmissionDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            paymentDate: values?.paymentDate
                ? moment.utc(values?.paymentDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            paidAmount: values?.paidAmount ? values?.paidAmount.toString().trim() : 0,
            status: values?.status,
            holdReasonObservations: values?.holdReasonObservations,
            utrNo: values?.utrNo,
        };

        try {
            const response = await updateInvoice(reqBody).unwrap();
            openNotification("Invoice updated successfully", "success");
            resetForm();
            setSubmitting(false);
            handleClose();
        } catch (error) {
            // openNotification(error?.error ?? "Something went wrong", "error");
            //console.log("API ERROR:", error);

            let errorMessage = "Something went wrong";

            if (error?.data?.errors) {
                errorMessage = Object.values(error.data.errors)
                    .flat()
                    .join(", ");
            } else if (error?.data?.title) {
                errorMessage = error.data.title;
            } else if (error?.error) {
                errorMessage = error.error;
            }
            openNotification(errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (
        <>
            <Dialog open={open}
                fullScreen
                PaperProps={{
                    sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
                }}
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        handleClose();
                    }
                }}
            >
                <DialogTitle sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #ddd",
                    backgroundColor: "#ECECEC",
                    pb: 2,
                    pt: 3,
                    width: "100%",
                }}>
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                            Edit Invoice
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={() => handleClose()}
                        sx={(theme) => ({
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <RxCross2 style={{ fontSize: "30px", color: "#140F36" }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>

                        <Grid size={{ md: 6, xs: 12 }}>
                            <Formik
                                initialValues={formData}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ values,
                                    errors,
                                    touched,
                                    handleChange,
                                    handleBlur,
                                    handleSubmit,
                                    isSubmitting,
                                    setFieldValue,
                                    setFieldTouched,
                                    setTouched, }) => (
                                    <form onSubmit={handleSubmit}>
                                        <Grid container spacing={2}>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Vendor Name</Typography>
                                                <Select
                                                    name="vendorName"
                                                    options={vendorNameOptions}
                                                    value={vendorNameOptions.find(opt => opt.value === String(values.vendorName)) || null}
                                                    onChange={(option) => setFieldValue("vendorName", option.value)}
                                                    onBlur={() => setFieldTouched("vendorName", true)}
                                                    placeholder="Select Vendor Name"
                                                />
                                                {touched.vendorName && errors.vendorName && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.vendorName}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>WO/Contract No.</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="woContractNo"
                                                    value={values.woContractNo}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter WO/Contract No."
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.woContractNo && errors.woContractNo && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.woContractNo}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Invoice Particulars</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="invoiceParticulars"
                                                    value={values.invoiceParticulars}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Invoice Particulars"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.invoiceParticulars && errors.invoiceParticulars && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.invoiceParticulars}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Type</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="type"
                                                    value={values.type}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Type"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.type && errors.type && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.type}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Invoice No.</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="invoiceNo"
                                                    value={values.invoiceNo}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Invoice No."
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.invoiceNo && errors.invoiceNo && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.invoiceNo}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Invoice Date
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="invoiceDate"
                                                    value={values.invoiceDate}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.invoiceDate && errors.invoiceDate && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.invoiceDate}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Total Invoice Amount (Inc GST)</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="totalInvoiceAmountIncGst"
                                                    value={values.totalInvoiceAmountIncGst}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Total Invoice Amount"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.totalInvoiceAmountIncGst && errors.totalInvoiceAmountIncGst && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.totalInvoiceAmountIncGst}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Received From Vendor
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="receivedFromVendor"
                                                    value={values.receivedFromVendor}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.receivedFromVendor && errors.receivedFromVendor && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.receivedFromVendor}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Invoice Received For PMO Review
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="invoiceReceivedForPmoReview"
                                                    value={values.invoiceReceivedForPmoReview}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.invoiceReceivedForPmoReview && errors.invoiceReceivedForPmoReview && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.invoiceReceivedForPmoReview}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    PMO Review
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="pmoReview"
                                                    value={values.pmoReview}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.pmoReview && errors.pmoReview && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.pmoReview}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    PMO Review After Comments Incorporated
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="pmoReviewAfterCommentsIncorporated"
                                                    value={values.pmoReviewAfterCommentsIncorporated}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.pmoReviewAfterCommentsIncorporated && errors.pmoReviewAfterCommentsIncorporated && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.pmoReviewAfterCommentsIncorporated}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Sent To Approver 1
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="sentToApprover1"
                                                    value={values.sentToApprover1}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.sentToApprover1 && errors.sentToApprover1 && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.sentToApprover1}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Approver 1</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="approver1"
                                                    value={values.approver1}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Approver 1"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.approver1 && errors.approver1 && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.approver1}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Approver 1 Approval Date
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="approver1ApprovalDate"
                                                    value={values.approver1ApprovalDate}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.approver1ApprovalDate && errors.approver1ApprovalDate && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.approver1ApprovalDate}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Sent To Approver 2
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="sentToApprover2"
                                                    value={values.sentToApprover2}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.sentToApprover2 && errors.sentToApprover2 && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.sentToApprover2}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Approver 2</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="approver2"
                                                    value={values.approver2}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Approver 2"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.approver2 && errors.approver2 && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.approver2}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Approver 2 Approval Date
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="approver2ApprovalDate"
                                                    value={values.approver2ApprovalDate}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.approver2ApprovalDate && errors.approver2ApprovalDate && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.approver2ApprovalDate}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Sent To Approver 3
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="sentToApprover3"
                                                    value={values.sentToApprover3}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.sentToApprover3 && errors.sentToApprover3 && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.sentToApprover3}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Approver 3</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="approver3"
                                                    value={values.approver3}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Approver 3"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.approver3 && errors.approver3 && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.approver3}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Approver 3 Approval Date
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="approver3ApprovalDate"
                                                    value={values.approver3ApprovalDate}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.approver3ApprovalDate && errors.approver3ApprovalDate && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.approver3ApprovalDate}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Sent To Approver 4
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="sentToApprover4"
                                                    value={values.sentToApprover4}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.sentToApprover4 && errors.sentToApprover4 && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.sentToApprover4}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Approver 4</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="approver4"
                                                    value={values.approver4}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Approver 4"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.approver4 && errors.approver4 && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.approver4}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Approver 4 Approval Date
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="approver4ApprovalDate"
                                                    value={values.approver4ApprovalDate}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.approver4ApprovalDate && errors.approver4ApprovalDate && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.approver4ApprovalDate}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Sent To Approver 5
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="sentToApprover5"
                                                    value={values.sentToApprover5}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.sentToApprover5 && errors.sentToApprover5 && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.sentToApprover5}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Approver 5</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="approver5"
                                                    value={values.approver5}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Approver 5"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.approver5 && errors.approver5 && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.approver5}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Approver 5 Approval Date
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="approver5ApprovalDate"
                                                    value={values.approver5ApprovalDate}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.approver5ApprovalDate && errors.approver5ApprovalDate && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.approver5ApprovalDate}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Finance Submission Date
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="financeSubmissionDate"
                                                    value={values.financeSubmissionDate}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.financeSubmissionDate && errors.financeSubmissionDate && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.financeSubmissionDate}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                    Payment Date
                                                </Typography>
                                                <TextField
                                                    type="date"
                                                    name="paymentDate"
                                                    value={values.paymentDate}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onClick={(e) => {
                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                        if (input) input.showPicker?.();
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        ...rightTextStyle,
                                                        "& input::-webkit-calendar-picker-indicator": {
                                                            display: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& input[type='date']": {
                                                            appearance: "none",
                                                            WebkitAppearance: "none",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                {CalenderSVG}
                                                            </Box>
                                                        ),
                                                        sx: {
                                                            backgroundColor: "#fff",
                                                            border: '1px solid',
                                                            borderColor: 'hsl(0, 0%, 80%)',
                                                            height: "40px",
                                                            width: { xs: "100%", md: "100%" },
                                                            borderRadius: "5px",
                                                        },
                                                    }}
                                                />
                                                {touched.paymentDate && errors.paymentDate && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.paymentDate}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Paid Amount</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="paidAmount"
                                                    value={values.paidAmount}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Paid Amount"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.paidAmount && errors.paidAmount && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.paidAmount}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Status</Typography>
                                                <Select
                                                    name="status"
                                                    options={statusOptions}
                                                    value={statusOptions.find(opt => opt.value === String(values.status)) || null}
                                                    onChange={(option) => setFieldValue("status", option.value)}
                                                    onBlur={() => setFieldTouched("status", true)}
                                                    placeholder="Select Status"
                                                />
                                                {touched.status && errors.status && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.status}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Hold Reason / Observations</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="holdReasonObservations"
                                                    value={values.holdReasonObservations}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Hold Reason / Observations"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.holdReasonObservations && errors.holdReasonObservations && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.holdReasonObservations}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>UTR No.</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="utrNo"
                                                    value={values.utrNo}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter UTR No."
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.utrNo && errors.utrNo && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.utrNo}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={12}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Button onClick={handleClose}
                                                        sx={{
                                                            textTransform: "none",
                                                            color: "#F15744",
                                                            fontSize: "14px",
                                                            lineHeight: "100%",
                                                            fontFamily: "Outfit, sans-serif",
                                                            borderRadius: "25px",
                                                            fontWeight: 500,
                                                            px: "20px",
                                                            py: "15px",
                                                        }}>
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        // loading={isLoading}
                                                        sx={{
                                                            backgroundColor: "#00344C",
                                                            borderRadius: "25px",
                                                            textTransform: "none",
                                                            fontWeight: 500,
                                                            fontSize: "14px",
                                                            lineHeight: "100%",
                                                            fontFamily: "Outfit, sans-serif",
                                                            color: "#FFFFFF",
                                                            px: "20px",
                                                            py: "15px",
                                                            display: "flex",
                                                            gap: 1,
                                                            "&.Mui-disabled": {
                                                                backgroundColor: "#00344C",
                                                                color: "#fff",
                                                                opacity: 0.7,
                                                            },
                                                        }}
                                                        disabled={isLoading}
                                                    >
                                                        {isLoading && <CircularProgress size={16} color="inherit" />}
                                                        Save
                                                    </Button>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </form>
                                )}
                            </Formik>
                        </Grid>

                        <Grid
                            size={{ md: 6, xs: 12 }}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                height: '90vh', // adjustable
                                border: "1px solid #e0e0e0",
                                borderRadius: 2,
                                overflow: "auto",
                            }}
                        >
                            <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
                                {/* Comment list */}
                                <InvoiceCommentList data={rowData} />
                            </Box>

                            <Box sx={{ borderTop: "1px solid #e0e0e0", p: 2 }}>
                                <AddInvoiceComment data={rowData} />
                            </Box>
                        </Grid>

                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EditInvoice;